import { useTranslation } from 'react-i18next';
import { connectorPlatformLabel } from '@/entities/media-connector';
import { FilterChipPopover } from '@/shared/ui';
import { DateRangePicker, type DateRange, type DatePreset } from '@/shared/ui/DateRangePicker';
import type { PerformanceFilters } from '../model/filter-params';

export interface PerformanceToolbarProps {
  filters: PerformanceFilters;
  /** Only the platforms this project's campaigns actually use. */
  availablePlatforms: string[];
  /** Only the channels this project's campaigns actually use. */
  availableChannels: string[];
  flightRange?: DateRange | null;
  onChange: (next: PerformanceFilters) => void;
}

/** Channels have no label map, and their stored values are already the words to show. */
function channelLabel(channel: string): string {
  return channel.charAt(0).toUpperCase() + channel.slice(1);
}

/**
 * Platform and channel options come from the project's own campaigns, so the filter can
 * never offer a choice that returns nothing — the same rule the Delivery job toolbar
 * follows.
 *
 * Each filter is one chip that opens its options, rather than every option sitting on the
 * toolbar as its own toggle: a project on five platforms across four channels put nine
 * controls in the row before the user had chosen anything. A chip also has somewhere to
 * show how many values are active, which a row of toggles has to spell out by highlighting
 * some and not others.
 *
 * A chip renders while it has options OR while it holds a selection. Dropping it the moment
 * its options empty would strand an active filter in the URL with nothing on screen to
 * clear it.
 */
export function PerformanceToolbar({
  filters,
  availablePlatforms,
  availableChannels,
  flightRange = null,
  onChange,
}: PerformanceToolbarProps) {
  const { t } = useTranslation('projects');
  const showPlatforms = availablePlatforms.length > 0 || filters.platforms.length > 0;
  const showChannels = availableChannels.length > 0 || filters.channels.length > 0;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="performance-toolbar">
      <DateRangePicker
        value={filters.range}
        preset={filters.preset}
        today={new Date().toISOString().slice(0, 10)}
        flightRange={flightRange}
        onChange={(range: DateRange, preset: DatePreset) => onChange({ ...filters, range, preset })}
      />

      {showPlatforms && (
        <FilterChipPopover<string>
          mode="multiple"
          label={t('performanceFilters.performanceToolbar.platforms')}
          value={filters.platforms}
          options={availablePlatforms.map((platform) => ({
            value: platform,
            label: connectorPlatformLabel(platform),
          }))}
          onChange={(platforms) => onChange({ ...filters, platforms: [...platforms] })}
          triggerAriaLabel={t('performanceFilters.performanceToolbar.filterByPlatform')}
          searchPlaceholder={t('performanceFilters.performanceToolbar.searchPlatforms')}
        />
      )}

      {showChannels && (
        <FilterChipPopover<string>
          mode="multiple"
          label={t('performanceFilters.performanceToolbar.channels')}
          value={filters.channels}
          options={availableChannels.map((channel) => ({
            value: channel,
            label: channelLabel(channel),
          }))}
          onChange={(channels) => onChange({ ...filters, channels: [...channels] })}
          triggerAriaLabel={t('performanceFilters.performanceToolbar.filterByChannel')}
          searchPlaceholder={t('performanceFilters.performanceToolbar.searchChannels')}
        />
      )}
    </div>
  );
}
